import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import NavBarTitles from "../../../components/Common/TabBar";
import Deletepopup from "../../../components/Modal/Deletepopup";

// Updated custom hook paths
import apiCall from "../../../components/Api/index";
import Actions from "../../../components/Common/Actions";
import AntdBreadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import ListPageLayout from "../../../components/Layout";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";

const Sitemap = () => {
  const navigate = useNavigate();
  const { category } = useParams();

  const addSiteMap = {
    id: "addnewpost",
    onClick: () => navigate(`/add-sitemap-form/${category}`),
    style: { width: "240px" },
    label: "Add Sitemap",
  };

  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  const [sitemapHeader, setSitemapHeader] = useState([]);

  const [loader, setLoader] = useState(true);
  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}sitemap/list`,
      initialParams: {
        sort,
        sitemap_category: category,
        // selectedValues,
      },
    });

  const getSitemapHeader = async () => {
    try {
      const result = await apiCall(`sitemap-category/list`, "POST", {
        active: true,
      });
      if (result.data?.rows) {
        setSitemapHeader(result.data?.rows);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getSitemapHeader();
  }, [category]);

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler("sitemap", fetchData, setShowDelete);

  const handleActive = (id, status) => {
    axios
      .patch(`${process.env.REACT_APP_API_URL}sitemap/update/active/${id}`, {
        active: !status,
      })
      .then((result) => {
        fetchData();
      });
  };

  const NavbarData = useMemo(() => {
    return sitemapHeader.map(({ title, slug, ...rest }) => ({
      name: title,
      navigatePath: `/sitemap/${slug}`,
    }));
  }, [category, sitemapHeader]);

  useEffect(() => {
    setLoader(true)
    fetchData();
  }, [pagination.skip, pagination.limit, sort, search, category]);

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Sitemap Name",
      dataIndex: "sitemapName",
      key: "sitemapName",
      fixed: "left",
    },
    {
      title: "Total URL's in Sitemap",
      dataIndex: "totalUrls",
      key: "totalUrls",
    },
    { title: "Sitemap Live URL", dataIndex: "liveUrl", key: "liveUrl" },
    { title: "Last Update On", dataIndex: "lastUpdateOn", key: "lastUpdateOn" },
    {
      title: "Previously Updated On",
      dataIndex: "previousUpdate",
      key: "previousUpdate",
    },
    {
      title: "Sitemap Updated Status",
      dataIndex: "updatedStatus",
      key: "updatedStatus",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Actions
          isEdit={true}
          recordActiveStatus={record.active}
          isActive={category !== "parent-sitemap"}
          onClickActive={() => handleActive(record.key, record.active)}
          isDelete={category !== "parent-sitemap"}
          onClickDelete={() => {
            setDeleteId(record.key);
            setShowDelete(true);
          }}
          onClickEdit={() =>
            navigate(`/edit-sitemap-form/${category}/${record.key}`)
          }
        />
      ),
    },
  ];

  const dataSource = records
    .slice(pagination.skip, pagination.skip + pagination.limit)
    .map((el, i) => ({
      key: el._id,
      serial: pagination.skip + i + 1,
      sitemapName: el.sitemap_name,
      totalUrls: el.total_url_sitemap,
      liveUrl: el.live_sitemap_url,
      lastUpdateOn: new Date(el.updatedAt).toLocaleString(),
      previousUpdate: el?.previousUpdate
        ? new Date(el.previousUpdate).toLocaleString()
        : "-",
      updatedStatus: el.active ? "Success" : "Failed",
      sitemapStatus: el.active ? "Inactive" : "Active",
      actions: el._id,
      active: el.active,
    }));

  const breadcrumbsList = useMemo(
    () => [
      {
        text: "Sitemap",
        link: "/sitemap/parent-sitemap",
      },
      {
        text: "Sitemap Analysis",
        link: "",
      },
    ],
    [],
  );
  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={
        <AntdBreadcrumbs
          className="healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={
        category !== "parent-sitemap" && <FilledButton props={addSiteMap} />
      }
      titles={<NavBarTitles titles={NavbarData} />}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource,
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}
    </ListPageLayout>
  );
};

export default Sitemap;
